import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import type React from "react";

import type { DispatchField } from "@storyboard/dispatch-view/view";
import type { FilterNode } from "@storyboard/driver-query/predicate";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { excludeCondition, onlyCondition } from "@/hooks/dispatch/adhocConditions";
import type { DispatchMenuAction, DispatchMenuEntry } from "@/hooks/dispatch/dispatchMenu";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";
import { zinc } from "@/ui/tokens";
import { dispatchMenuItems } from "./dispatchMenuItems";

/**
 * A field the menu can offer conditions on: its key, header text, and how to name a value of it. `valueOnly` says
 * the value's name carries the field's meaning on its own ("Acknowledged"), so the entry reads without the label.
 */
export type RowMenuField = {
  key: DispatchField;
  label: string;
  valueLabel: (value: string) => string;
  valueOnly?: boolean;
};

/**
 * The row context menu: the acknowledgement and task entries the History menus share, in their order, then after a
 * divider "Only" (keep rows equal to it) and "Exclude" (drop them) for a value the row carries. The conditions are how a
 * dispatcher adds one the view's controls do not offer; the strip shows it and clears it.
 *
 * A right-click on a cell is about that cell, so the menu offers the clicked column's value alone. When the right-click
 * missed every cell, or the clicked cell has nothing to condition on, every visible field the row carries a value for
 * is offered instead.
 */
export default function DispatchRowMenu({
  row,
  fields,
  columnKey,
  anchorPosition,
  onClose,
  onAddCondition,
  actions,
  onAction,
}: {
  row: DispatchTableRow | null;
  fields: readonly RowMenuField[];
  /** The field key of the cell right-clicked, or `null` when the menu was opened for the row as a whole. */
  columnKey: string | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onAddCondition: (condition: FilterNode) => void;
  /** The acknowledgement and task entries for the row, as `rowMenuFor` lists them. */
  actions: readonly DispatchMenuEntry[];
  onAction: (action: DispatchMenuAction) => void;
}) {
  const entriesFor = (candidates: readonly RowMenuField[]) =>
    row
      ? candidates.flatMap((field) => {
          const raw = row.record[field.key];
          const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
          return values.filter((value) => value !== "").map((value) => ({ field, value }));
        })
      : [];
  const scoped = columnKey ? entriesFor(fields.filter((field) => field.key === columnKey)) : [];
  const entries = scoped.length > 0 ? scoped : entriesFor(fields);
  const add = (condition: FilterNode) => {
    onAddCondition(condition);
    onClose();
  };
  // The entry waits a frame: closing the menu hands focus back to the page, and a picker opened in the same commit would
  // lose its autofocused field to that hand-back.
  const pick = (action: DispatchMenuAction) => {
    onClose();
    requestAnimationFrame(() => onAction(action));
  };
  return (
    <Menu
      open={anchorPosition != null && (entries.length > 0 || actions.length > 0)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      slotProps={{
        paper: {
          "data-sb": TestIds.dispatch.rowMenu,
          sx: { bgcolor: zinc[800], color: "white", minWidth: 240 },
        } as React.ComponentProps<typeof Menu>["PaperProps"],
        list: { dense: true },
      }}
    >
      {dispatchMenuItems(actions, pick)}
      {actions.length > 0 && entries.length > 0 && <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />}
      {entries.map(({ field, value }) => {
        const shown = field.valueLabel(value);
        const what = field.valueOnly ? (
          <b>{shown}</b>
        ) : (
          <>
            <b>{field.label}</b>: {shown}
          </>
        );
        return [
          <MenuItem key={`${field.key}:${value}:only`} onClick={() => add(onlyCondition(field.key, value))}>
            <Typography sx={{ fontSize: "0.8125rem" }}>Only {what}</Typography>
          </MenuItem>,
          <MenuItem key={`${field.key}:${value}:exclude`} onClick={() => add(excludeCondition(field.key, value))}>
            <Typography sx={{ fontSize: "0.8125rem", color: zinc[300] }}>Exclude {what}</Typography>
          </MenuItem>,
        ];
      })}
    </Menu>
  );
}
